import { useCallback, useContext, useMemo, useState } from "react";
import { ProfileContext } from "../../../context/ProfileProvider";
import { startDiditKybFlow } from "../../../customComponents/Didit/startDiditKybFlow";
import { pickKycSessionFields } from "../../../utils/kycSessionPayload";

/**
 * KYC-style CTA rules for KYB:
 * - NOT_STARTED / EXPIRED → Start
 * - RESUBMISSION_REQUESTED → Start verification (forceNew)
 * - PENDING + In Progress → Continue
 * - PENDING + In Review → hide start button
 * - REJECTED → Try Again (forceNew)
 * - APPROVED → no verify CTA
 */
export function getKybCtaConfig({ canonicalStatus, diditStatus } = {}) {
  const st = String(canonicalStatus || "").toUpperCase();
  const vendor = String(diditStatus || "").trim();
  const inProgress = vendor === "In Progress";

  if (st === "APPROVED") {
    return { show: false, label: "", forceNew: false, resume: false, variant: "none" };
  }
  if (st === "REJECTED") {
    return { show: true, label: "Try Again", forceNew: true, resume: false, variant: "retry" };
  }
  if (st === "RESUBMISSION_REQUESTED") {
    return { show: true, label: "Start verification", forceNew: true, resume: false, variant: "resubmit" };
  }
  if (st === "PENDING") {
    if (inProgress) {
      return { show: true, label: "Continue KYB Verification", forceNew: false, resume: true, variant: "continue" };
    }
    return { show: false, label: "", forceNew: false, resume: false, variant: "review" };
  }
  if (st === "EXPIRED") {
    return { show: true, label: "Start KYB Verification", forceNew: true, resume: false, variant: "start" };
  }
  // NOT_STARTED / unknown
  return { show: true, label: "Start KYB Verification", forceNew: false, resume: false, variant: "start" };
}

export function useKybVerifyAction({
  kybStatusRes,
  canonicalStatus,
  diditStatus,
  onFlowEnd,
  userDetails: userDetailsProp,
} = {}) {
  const [busy, setBusy] = useState(false);
  const profileCtx = useContext(ProfileContext) || {};
  const userDetails = useMemo(
    () => (profileCtx.userDetails && Object.keys(profileCtx.userDetails).length
      ? profileCtx.userDetails
      : (userDetailsProp || {})),
    [profileCtx.userDetails, userDetailsProp]
  );

  const cta = useMemo(
    () => getKybCtaConfig({ canonicalStatus, diditStatus }),
    [canonicalStatus, diditStatus]
  );

  const startVerify = useCallback(async (overrides = {}) => {
    if (busy) return false;
    setBusy(true);
    try {
      const forceNew = overrides.forceNew != null ? overrides.forceNew : cta.forceNew;
      const resumeUrl = pickKycSessionFields(kybStatusRes).diditUrl;
      const shouldResume = overrides.resume != null ? overrides.resume : cta.resume;
      return await startDiditKybFlow({
        userDetails,
        onFlowEnd,
        forceNew: forceNew === true ? true : undefined,
        existingDiditUrl: shouldResume && !forceNew ? resumeUrl : undefined,
      });
    } finally {
      setBusy(false);
    }
  }, [busy, cta, kybStatusRes, userDetails, onFlowEnd]);

  return { busy, cta, startVerify, userDetails };
}
